import React, { useRef, useState } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { FreeMode, Navigation, Pagination } from 'swiper/modules';
import Icona from 'components/corporatiu/Icona';
import { getVisibleSlides } from './funcions';
import { useScript } from 'hooks/useScript';
import { getPropsSwiper } from './breakpoints';
import Mosaic from 'components/corporatiu/Mosaic';
import Titol from 'components/corporatiu/Titol';
import Text from 'components/corporatiu/Text';
import Boto from 'components/corporatiu/Boto';
import __isUndefined from 'lodash/isUndefined';
import __isEmpty from 'lodash/isEmpty';
import clsx from 'clsx';
import 'swiper/css';
import 'swiper/css/free-mode';
import 'swiper/css/grid';
import 'swiper/css/navigation';
import styles from './slider.module.scss';

const MoreItemsButton = (props) => {
    const { mostrarBotoMesItems, propsEnllac, textBotoMes } = props;
    if (!mostrarBotoMesItems || __isEmpty(propsEnllac)) return <></>;

    const defaultPropsEnllac = {
        posicio: 'dreta',
        icona: 'fletxa_simple_dreta',
        iconaInline: true
    };

    const finalPropsEnllac = { ...defaultPropsEnllac, ...propsEnllac };
    const { slug, slug2, linkTipus, href, posicio, className, icona } = finalPropsEnllac;

    const stylesBoto = clsx({
        [styles.btnMoreItemsContent]: true,
        [styles[posicio]]: !!posicio,
        [className]: !!className
    });

    return (
        <div className={stylesBoto}>
            <Boto
                text={textBotoMes}
                estil='transparent'
                mida='s'
                tema='alignDreta'
                icona={icona}
                linkSlug={slug}
                slug2={slug2}
                type={linkTipus}
                href={href}
                dataTestId={`boto-tots`}
            />
        </div>
    );
};

MoreItemsButton.defaultProps = {
    mostrarBotoMesItems: false,
    propsEnllac: {}
};

const Slider = (props) => {
    const {
        titolClass,
        descripcio,
        format,
        className,
        estil,
        estilModul,
        tema = 'corporatiu',
        titol = '',
        fletxes = 'default',
        tag = 'h2',
        tipus
    } = props;

    const items = props.children;
    const navigationPrev = useRef(null);
    const navigationNext = useRef(null);
    const swiperRef = useRef(null);
    const swiperProps = useRef({});
    const [swiperInit, setSwiperInit] = useState(false);
    const [swiperMoved, setSwiperMoved] = useState(false);
    const [sliderPosition, setSliderPosition] = useState(0);
    const [visibleSlides, setVisibleSlides] = useState(0);
    const paginationRef = useRef(null);
    const { isJavaScriptClientEnabled } = useScript();
    const sliderId = 'id' + (titol + format).replace(/^[^a-z]+|[^\w:.-]+/gi, '');
    let resizeTimeOut;

    if (__isUndefined(props.children) || props.children.length <= 0) return <></>;

    const stylesSwiper = clsx({
        [styles.innerSlider]: true,
        [styles[estil]]: !!estil,
        [styles[tema]]: !!tema,
        [styles.dreta]: sliderPosition === 1,
        [styles.esquerra]: sliderPosition === 0,
        [className]: !!className
    });

    const stylesModul = clsx({
        [styles.slider]: true,
        [styles[estilModul]]: !!estilModul,
        [styles.init]: swiperInit,
        [styles.noTitol]: __isEmpty(titol) && fletxes !== 'mig',
        [styles.siDescripcio]: !__isEmpty(descripcio)
    });

    const stylesMosaicNoJavascript = clsx({
        [styles.mosaicNoJavascript]: true,
        [styles.sliderDefault]: true,
        [styles[`columns-${items.length}`]]: !!items,
        [className]: !!className
    });

    const stylesFletxes = clsx({
        [styles.contentFletxes]: true,
        [styles[tema]]: !!tema,
        [styles[`fletxes-${fletxes}`]]: !!fletxes
    });

    const stylesIconaFletxa = clsx({
        [styles.iconaFletxa]: true,
        [styles.fletxaMig]: fletxes === 'mig'
    });

    const _buildSlide = (component, i) => {
        const generateUniqueId = () => `${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
        const key = component.key || generateUniqueId();

        return (
            <SwiperSlide
                tag='li'
                key={`slider-${key}`}
                virtualIndex={i}
                data-testid={`slider-${key}`}
                //onKeyDown={(event) => handleKeyDown(event, i + 1)}
            >
                {component}
            </SwiperSlide>
        );
    };

    const _iterateItems = (items) => {
        if (!swiperMoved) {
            let sliderItems = !__isEmpty(items)
                ? Array.from(items)
                      .slice(0, visibleSlides)
                      .map((component, i) => _buildSlide(component, i))
                : null;
            if (sliderItems?.length < items.length) {
                for (let i = 0; i < items.length - visibleSlides; i++) {
                    sliderItems = sliderItems.concat(
                        <SwiperSlide
                            tag='li'
                            key={`slider-${i}`}
                            virtualIndex={i}
                            data-testid={`slider-${i}`}
                            //onKeyDown={(event) => handleKeyDown(event, i + 1)}
                        />
                    );
                }
            }

            return sliderItems;
        } else {
            return !__isEmpty(items) ? Array.from(items).map((component, i) => _buildSlide(component, i)) : null;
        }
    };

    const _setFletxes = (swiper) => {
        if (tipus === 'destacat' && fletxes === 'mig') {
            window.requestAnimationFrame(() => {
                const firstFeaturedImage = swiper?.slides?.[0]?.querySelector('img');

                if (!firstFeaturedImage) {
                    return;
                }

                const logImageHeight = () => {
                    const imageHeight = firstFeaturedImage.offsetHeight;
                    const buttonTop = `${imageHeight / 2}px`;

                    if (navigationPrev.current) {
                        navigationPrev.current.style.top = buttonTop;
                    }

                    if (navigationNext.current) {
                        navigationNext.current.style.top = buttonTop;
                    }
                };

                if (firstFeaturedImage.complete) {
                    logImageHeight();
                } else {
                    firstFeaturedImage.addEventListener('load', logImageHeight, { once: true });
                }
            });
        }
    };

    const _renderSwiper = () => {
        const propsSwiper = getPropsSwiper(format);

        const BUTTON_ROLE = 'button, [role="button"]';

        const _slidesIndexOf = (swiper) => {
            const slidesInSight = swiper.slides.filter((slide) => slide.classList.contains('swiper-slide-visible'));
            const lastVisibleSlideIndex = swiper.visibleSlidesIndexes[swiper.visibleSlidesIndexes.length - 1];
            const lastVisibleSlidePosition = swiper.slidesGrid[lastVisibleSlideIndex];
            const lastVisibleSlideWidth = swiper.slidesSizesGrid[lastVisibleSlideIndex];
            const lastVisibleSlideEnd = lastVisibleSlidePosition + lastVisibleSlideWidth;
            const isLastSlideOverFlowing = swiper.size - swiper.translate < lastVisibleSlideEnd;
            const firstVisibleSlideIndex = swiper.visibleSlidesIndexes[0];
            const firstVisibleSlidePosition = swiper.slidesGrid[firstVisibleSlideIndex];
            const isFirstSlideOverFlowing = swiper.translate + firstVisibleSlidePosition < 0;

            slidesInSight.forEach((slide) => {
                if (slide.querySelectorAll('a').length > 0) {
                    slide.querySelectorAll('a').forEach((link) => {
                        if (slidesInSight.length === 2 && isFirstSlideOverFlowing && isLastSlideOverFlowing) {
                            if (slide === slidesInSight[0]) {
                                link.setAttribute('tabindex', '0');
                            } else {
                                link.setAttribute('tabindex', '-1');
                            }
                        } else {
                            if (isLastSlideOverFlowing && slide === slidesInSight[slidesInSight.length - 1]) {
                                link.setAttribute('tabindex', '-1');
                            } else if (isFirstSlideOverFlowing && slide === slidesInSight[0]) {
                                link.setAttribute('tabindex', '-1');
                            } else {
                                link.setAttribute('tabindex', '0');
                            }
                        }
                    });
                } else if (slide.querySelectorAll(BUTTON_ROLE).length > 0) {
                    slide.querySelectorAll(BUTTON_ROLE).forEach((button) => {
                        if (slidesInSight.length === 2 && isFirstSlideOverFlowing && isLastSlideOverFlowing) {
                            if (slide === slidesInSight[0]) {
                                button.setAttribute('tabindex', '0');
                            } else {
                                button.setAttribute('tabindex', '-1');
                            }
                        } else {
                            if (isLastSlideOverFlowing && slide === slidesInSight[slidesInSight.length - 1]) {
                                button.setAttribute('tabindex', '-1');
                            } else if (isFirstSlideOverFlowing && slide === slidesInSight[0]) {
                                button.setAttribute('tabindex', '-1');
                            } else {
                                button.setAttribute('tabindex', '0');
                            }
                        }
                    });
                }
            });
            swiper.slides.forEach((slide) => {
                if (!slide.classList.contains('swiper-slide-visible')) {
                    if (slide.querySelectorAll('a').length > 0) {
                        slide.querySelectorAll('a').forEach((link) => {
                            link.setAttribute('tabindex', '-1');
                        });
                    } else if (slide.querySelectorAll(BUTTON_ROLE).length > 0) {
                        slide.querySelectorAll(BUTTON_ROLE).forEach((button) => {
                            button.setAttribute('tabindex', '-1');
                        });
                    }
                }
            });
        };

        return (
            <>
                <div className={styles.contSlider}>
                    <Swiper
                        {...propsSwiper}
                        wrapperTag='ul'
                        ref={swiperRef}
                        pagination={{
                            el: paginationRef.current,
                            type: 'fraction',
                            renderFraction: (currentClass, totalClass) => {
                                return `<span class="${currentClass}"></span> de <span class="${totalClass}"></span>`;
                            }
                        }}
                        navigation={{
                            prevEl: navigationPrev.current,
                            nextEl: navigationNext.current
                        }}
                        onBeforeInit={(swiper) => {
                            swiper.params.navigation.prevEl = navigationPrev.current;
                            swiper.params.navigation.nextEl = navigationNext.current;
                            setVisibleSlides(getVisibleSlides(propsSwiper));
                        }}
                        onAfterInit={(swiper) => {
                            setSwiperInit(true);
                            _slidesIndexOf(swiper);
                            _setFletxes(swiper);
                        }}
                        modules={[Navigation, FreeMode, Pagination]}
                        className={stylesSwiper}
                        updateOnWindowResize={false}
                        onProgress={(swiper) => {
                            setSliderPosition(swiper.progress);
                            _slidesIndexOf(swiper);
                        }}
                        onSwiper={(swiper) => {
                            setTimeout(() => {
                                swiperProps.current = {
                                    slidesPerView: swiper.params?.slidesPerView,
                                    slidesLength: swiper.slides?.length
                                };
                            }, 100);
                            window.addEventListener('resize', function () {
                                clearTimeout(resizeTimeOut);
                                resizeTimeOut = setTimeout(() => {
                                    setVisibleSlides(getVisibleSlides(propsSwiper));
                                    swiper.update();
                                    _setFletxes(swiper);
                                }, 100);
                            });
                        }}
                        onSlideChange={() => {
                            setSwiperMoved(true);
                        }}
                        data-testid={`slider-${sliderId}`}
                    >
                        {_iterateItems(items)}
                    </Swiper>
                </div>
                <div className={stylesFletxes}>
                    <button ref={navigationPrev} className={styles.anterior}>
                        <Icona
                            icona='fletxa_esquerra'
                            isBlanc={true}
                            alt='Anterior'
                            mida={16}
                            inline={true}
                            className={stylesIconaFletxa}
                            tabIndex={0}
                        />
                    </button>
                    <div className={styles.index} ref={paginationRef}></div>
                    <button ref={navigationNext} className={styles.seguent}>
                        <Icona
                            icona='fletxa_dreta'
                            isBlanc={true}
                            alt='Següent'
                            mida={16}
                            inline={true}
                            className={`${stylesIconaFletxa} ${styles.iconadreta}`}
                            tabIndex={0}
                        />
                    </button>
                </div>
            </>
        );
    };

    return !__isEmpty(items) ? (
        <section className={stylesModul} aria-labelledby={sliderId}>
            {titol && (
                <div className={styles.infoTitol}>
                    <Titol titol={titol} sliderId={sliderId} tag={tag} estil={titolClass} />
                    {descripcio && <Text className={styles.descripcio} text={descripcio} tag='div' />}
                </div>
            )}
            <div className={styles.wrapperSlider}>
                {isJavaScriptClientEnabled ? (
                    _renderSwiper()
                ) : (
                    <Mosaic numColumns={4} numElementsDisplay={4} className={stylesMosaicNoJavascript}>
                        {items}
                    </Mosaic>
                )}
            </div>
            <MoreItemsButton {...props} />
        </section>
    ) : (
        <></>
    );
};

export default Slider;
